import { createContext, useContext, useEffect, useMemo, useState } from 'react'

const STORAGE_KEY = 'downloadQueue'

const DownloadQueueContext = createContext()

const itemId = item => `${item.bucket}:${item.key}`

const loadStoredItems = () => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    const parsed = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

const DownloadQueueProvider = ({ children }) => {
  const [items, setItems] = useState(loadStoredItems)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)

  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(items))
    } catch {
      /* storage unavailable — queue stays in memory */
    }
  }, [items])

  const addItems = newItems => {
    setItems(prev => {
      const existing = new Set(prev.map(itemId))
      const additions = newItems.filter(item => !existing.has(itemId(item)))
      return additions.length ? [...prev, ...additions] : prev
    })
  }

  const removeItem = item => {
    setItems(prev => prev.filter(i => itemId(i) !== itemId(item)))
  }

  const clearQueue = () => setItems([])

  const queuedKeys = useMemo(() => new Set(items.map(itemId)), [items])
  const isQueued = item => queuedKeys.has(itemId(item))

  return (
    <DownloadQueueContext.Provider
      value={{
        items,
        addItems,
        removeItem,
        clearQueue,
        isQueued,
        isDrawerOpen,
        openDrawer: () => setIsDrawerOpen(true),
        closeDrawer: () => setIsDrawerOpen(false),
        toggleDrawer: () => setIsDrawerOpen(prev => !prev),
      }}
    >
      {children}
    </DownloadQueueContext.Provider>
  )
}

const useDownloadQueue = () => {
  const ctx = useContext(DownloadQueueContext)
  if (!ctx)
    throw new Error('useDownloadQueue must be used within DownloadQueueProvider')
  return ctx
}

export { DownloadQueueProvider, useDownloadQueue }
